import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { useCharacter, useCharacters } from '../hooks/useCharacters';
import { useCampaigns } from '../hooks/useCampaigns';
import { PaizoFooter } from './PaizoFooter';

/** Extracts campaign info when viewing a character that belongs to a campaign. */
function useCampaignBreadcrumb() {
  const location = useLocation();
  const match = location.pathname.match(/^\/characters\/([^/]+)/);
  const characterId = match?.[1] === 'new' ? null : match?.[1] ?? null;

  // Use the same detail query the CharacterPage uses — already cached when on that page
  const { data: character } = useCharacter(characterId ?? '');
  const { data: campaigns } = useCampaigns();

  if (!character?.campaignMemberId || !campaigns) return null;

  const campaign = campaigns.find((c) =>
    c.members.some((m) => m.id === character.campaignMemberId),
  );
  return campaign ?? null;
}

/** When viewing a campaign page, finds the current user's character in that campaign. */
function useMyCharacterInCampaign() {
  const location = useLocation();
  const match = location.pathname.match(/^\/campaigns\/([^/]+)/);
  const campaignId = match?.[1] ?? null;
  const { data: campaigns } = useCampaigns();
  const { data: characters } = useCharacters();

  if (!campaignId || !campaigns || !characters) return null;

  const campaign = campaigns.find((c) => c.id === campaignId);
  if (!campaign) return null;

  // Find the member IDs that belong to this campaign
  const memberIds = new Set(campaign.members.map((m) => m.id));

  // Find user's character whose campaignMemberId matches a member in this campaign
  return characters.find((c) => c.campaignMemberId && memberIds.has(c.campaignMemberId)) ?? null;
}

export function Layout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const isSubPage = location.pathname !== '/';
  const campaign = useCampaignBreadcrumb();
  const myCharacter = useMyCharacterInCampaign();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {isSubPage && (
              <Link to="/" className="text-gray-500 hover:text-gray-300 transition-colors shrink-0" aria-label="Back to dashboard">
                &larr;
              </Link>
            )}
            <Link to="/" className="text-lg font-bold tracking-tight text-pf-500 hover:text-pf-400 transition-colors shrink-0">
              CharBuilder
            </Link>
            {campaign && (
              <nav className="flex items-center gap-1.5 text-sm min-w-0 ml-1">
                <span className="text-gray-600">/</span>
                <Link
                  to={`/campaigns/${campaign.id}`}
                  className="text-gray-400 hover:text-white transition-colors truncate"
                >
                  {campaign.name}
                </Link>
                <span className="text-gray-600 hidden sm:inline">/</span>
                <Link
                  to={`/campaigns/${campaign.id}#party`}
                  className="text-gray-500 hover:text-gray-200 transition-colors hidden sm:inline"
                >
                  Party
                </Link>
                <span className="text-gray-600 hidden sm:inline">/</span>
                <Link
                  to={`/campaigns/${campaign.id}#inventory`}
                  className="text-gray-500 hover:text-gray-200 transition-colors hidden sm:inline"
                >
                  Loot
                </Link>
              </nav>
            )}
            {myCharacter && (
              <nav className="flex items-center gap-1.5 text-sm min-w-0 ml-1">
                <span className="text-gray-600">/</span>
                <Link
                  to={`/characters/${myCharacter.id}`}
                  className="text-gray-400 hover:text-white transition-colors truncate"
                >
                  {myCharacter.name}
                </Link>
              </nav>
            )}
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Link to="/account" className="text-sm text-gray-400 hover:text-white transition-colors">
              {user?.username}
            </Link>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>
      <PaizoFooter />
    </div>
  );
}
